import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import React, { ChangeEvent, useState, useCallback, useEffect, useMemo } from 'react';

interface Props {
	inputPlaceholder: string;
	value?: number;
	name: string;
	errorMessage?: string;
	displayUnit?: string;
	inputId?: string;
	tabIndex?: number;
	getDisplayUnit?: (value: number | string | null) => string;
	onChange: (value: number | string | null) => void;
	dataTestId?: string;
}

const extractDisplayUnit = (string: string) => {
	const match = string.match(/[^\d\s]*$/);
	return match ? match[0] : '';
};

export const NumberInput: React.FC<Props> = (props: Props) => {
	const [displayValue, setDisplayValue] = useState<string>('');

	const displayValueFromProps = useMemo(() => {
		// eslint-disable-next-line eqeqeq
		if (props.value == undefined) {
			return '';
		}

		if (props.displayUnit) {
			return `${props.value}${props.displayUnit}`;
		}

		if (props.getDisplayUnit) {
			return `${props.value}${props.getDisplayUnit(props.value)}`;
		}

		return `${props.value}`;
	}, [props.value, props.inputId, props.displayUnit, props.getDisplayUnit]);

	useEffect(() => setDisplayValue(displayValueFromProps), [displayValueFromProps]);

	const onChange = (event: ChangeEvent<HTMLInputElement>) => {
		setDisplayValue(event.target.value);
	};

	const onBlur = useCallback(() => {
		const unit = extractDisplayUnit(displayValue);
		const numericValue = parseFloat(displayValue.replace(unit, '').trim());

		if (numericValue !== props.value) {
			if (displayValue === '') {
				props.onChange(null);
			} else if (isNaN(numericValue)) {
				props.onChange(displayValue);
			} else {
				props.onChange(numericValue);
			}
		}

		if (unit === props.displayUnit?.trim() || !props.displayUnit || !displayValue) {
			return;
		}

		setDisplayValue(`${numericValue}${props.displayUnit}`);
	}, [displayValue, props.onChange, props.displayUnit]);

	return (
		<>
			<input
				className="mt-2 w-full h-10 box-border border-solid p-2 rounded-md border border-gray-300 hover:border-gray-400 placeholder:text-sm text-sm"
				placeholder={props.inputPlaceholder}
				value={displayValue}
				onChange={onChange}
				onBlur={onBlur}
				name={props.name}
				tabIndex={props.tabIndex}
				data-testid={props.dataTestId}
			/>
			{props.errorMessage && (
				<div style={{ color: defaultTheme.v3.colors.red[6] }} className="mt-2 text-sm">
					{props.errorMessage}
				</div>
			)}
		</>
	);
};
